import React, { useEffect, useMemo, useState, useRef } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import { playChime, unlockAudio } from '../utils/chime';

function BarStationDisplay({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [scope, setScope] = useState({
    chain_id: selectedProperty?.chain_id || '',
    property_id: selectedProperty?.id || ''
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  // ER-MOM-05: instant new-order alert (WebSocket push) + visual highlight.
  const [wsConnected, setWsConnected] = useState(false);
  const [newOrderIds, setNewOrderIds] = useState(() => new Set());
  const prevIdsRef = useRef(new Set());
  const audioEnabledRef = useRef(audioEnabled);
  const wsConnectedRef = useRef(false);
  const loadBarOrdersRef = useRef(null);
  useEffect(() => { audioEnabledRef.current = audioEnabled; });
  useEffect(() => { wsConnectedRef.current = wsConnected; });
  const markNewOrder = (id) => {
    if (id == null) return;
    setNewOrderIds((prev) => { const n = new Set(prev); n.add(id); return n; });
    setTimeout(() => setNewOrderIds((prev) => { const n = new Set(prev); n.delete(id); return n; }), 30000);
  };

  const normalizeStatus = (value) => {
    const raw = String(value || '').toUpperCase();
    if (raw === 'SENT_TO_BAR' || raw === 'RAISED') return 'RAISED';
    if (raw === 'PREPARING' || raw === 'READY' || raw === 'SERVED') return raw;
    return '';
  };

  const scopeHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': scope.chain_id,
    'x-property-id': scope.property_id
  });

  const loadBarOrders = async () => {
    if (!scope.chain_id || !scope.property_id) return setRows([]);
    try {
      const res = await fetch('/api/orders', { headers: scopeHeaders(), cache: 'no-store' });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) {
        const apiError = data?.error || 'Failed to load BAR queue';
        return setError(apiError);
      }
      const allOrders = Array.isArray(data) ? data : [];
      const openBarOrders = allOrders.filter((order) => {
        const billStatus = String(order.bill_status || 'OPEN').toUpperCase();
        const normalized = normalizeStatus(order.bar_station_status || order.status);
        return billStatus === 'OPEN' && Boolean(normalized);
      });

      const detailedRows = await Promise.all(openBarOrders.map(async (order) => {
        try {
          const detailRes = await fetch(`/api/orders/${order.id}`, { headers: scopeHeaders(), cache: 'no-store' });
          const detailData = await detailRes.json().catch(() => ({}));
          const detailItems = detailRes.ok && Array.isArray(detailData.items) ? detailData.items : [];
          return {
            id: order.id,
            kot_no: order.id,
            station_id: null,
            station_name: 'BAR',
            status: normalizeStatus(order.bar_station_status || order.status) || 'RAISED',
            raised_at: order.order_date,
            table_no: order.table_name,
            order_type: order.order_type,
            guest_name: order.guest_name,
            room_number: order.room_number,
            check_id: detailItems[0]?.check_id || null,
            check_display: detailItems[0]?.check_label || detailItems[0]?.check_code || detailItems[0]?.check_id || null,
            items: detailItems.map((item) => ({
              item_name: item.menu_item_name || item.menu_item_code || 'Item',
              qty: Number(item.qty_servings || 0),
              special_instructions: item.remarks || ''
            }))
          };
        } catch {
          return {
            id: order.id,
            kot_no: order.id,
            station_id: null,
            station_name: 'BAR',
            status: normalizeStatus(order.bar_station_status || order.status) || 'RAISED',
            raised_at: order.order_date,
            table_no: order.table_name,
            order_type: order.order_type,
            guest_name: order.guest_name,
            room_number: order.room_number,
            check_id: null,
            check_display: null,
            items: []
          };
        }
      }));

      let newRows = detailedRows;
      if (statusFilter) {
        newRows = newRows.filter((row) => normalizeStatus(row.status) === statusFilter);
      }

      // Detect newly-raised orders by id (robust) — for the visual highlight and,
      // as a fallback, the chime when the WebSocket is disconnected.
      const raisedIds = new Set(newRows.filter((r) => normalizeStatus(r.status) === 'RAISED').map((r) => r.id));
      const freshIds = [...raisedIds].filter((id) => !prevIdsRef.current.has(id));
      if (prevIdsRef.current.size > 0 && freshIds.length) {
        if (audioEnabledRef.current && !wsConnectedRef.current) playChime(2);
        freshIds.forEach(markNewOrder);
      }
      prevIdsRef.current = raisedIds;
      setRows(newRows);
    } catch {
      setError('Network error while loading BAR queue');
    }
  };
  loadBarOrdersRef.current = loadBarOrders;

  useEffect(() => {
    if (selectedProperty?.chain_id && selectedProperty?.id) {
      setScope((prev) => ({ ...prev, chain_id: selectedProperty.chain_id, property_id: selectedProperty.id }));
    } else {
      setScope({ chain_id: '', property_id: '' });
      setRows([]);
    }
  }, [selectedProperty]);

  useEffect(() => { loadBarOrders(); }, [scope.chain_id, scope.property_id, statusFilter]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => { loadBarOrders(); }, 5000);
    return () => clearInterval(timer);
  }, [autoRefresh, scope.chain_id, scope.property_id, statusFilter]);

  useEffect(() => {
    const onKotRaised = () => {
      setTimeout(() => loadBarOrders(), 500);
    };
    window.addEventListener('kot-raised', onKotRaised);
    return () => window.removeEventListener('kot-raised', onKotRaised);
  }, [scope.chain_id, scope.property_id, statusFilter]);

  // ER-MOM-05: instant "new bar order" push via the staff WebSocket (property-wide
  // room — the bar display shows all bar orders). Falls back to the 5s poll.
  useEffect(() => {
    if (!scope.property_id || !token) return undefined;
    let ws = null, reconnectTimer = null, closed = false, attempt = 0;
    const scheduleReconnect = () => {
      if (closed) return;
      attempt += 1;
      reconnectTimer = setTimeout(connect, Math.min(1000 * 2 ** attempt, 30000));
    };
    const connect = () => {
      if (closed) return;
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${proto}://${window.location.host}/ws/kitchen`
        + `?token=${encodeURIComponent(token)}&property_id=${encodeURIComponent(scope.property_id)}`;
      try { ws = new WebSocket(url); } catch { scheduleReconnect(); return; }
      ws.onopen = () => { attempt = 0; setWsConnected(true); };
      ws.onmessage = (evt) => {
        let msg;
        try { msg = JSON.parse(evt.data); } catch { return; }
        if (msg?.type !== 'BAR_ORDER_RAISED') return;
        if (audioEnabledRef.current) playChime(2);
        if (msg.order_id != null) markNewOrder(msg.order_id);
        setMessage(`🔔 New bar order — #${msg.order_id ?? ''}`);
        if (loadBarOrdersRef.current) loadBarOrdersRef.current();
      };
      ws.onclose = () => { setWsConnected(false); scheduleReconnect(); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
    };
  }, [scope.property_id, token]);

  const performAction = async (id, action) => {
    if (!id || !action) return;
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/orders/${id}/bar-${action}`, { method: 'POST', headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || `Failed to mark ${action}`);
      setMessage(`Order ${action} success`);
      loadBarOrders();
    } catch {
      setError(`Network error while ${action}`);
    }
  };

  const formatAge = (raisedAt) => {
    if (!raisedAt) return '';
    const minutes = Math.floor((Date.now() - new Date(raisedAt).getTime()) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 min';
    return `${minutes} mins`;
  };

  const getAgeColor = (raisedAt) => {
    if (!raisedAt) return '#7f8c8d';
    const minutes = Math.floor((Date.now() - new Date(raisedAt).getTime()) / 60000);
    if (minutes < 5) return '#27ae60';
    if (minutes < 10) return '#f39c12';
    if (minutes < 15) return '#e67e22';
    return '#e74c3c';
  };

  const canStart = (row) => normalizeStatus(row?.status) === 'RAISED';
  const canReady = (row) => normalizeStatus(row?.status) === 'PREPARING';
  const canServe = (row) => normalizeStatus(row?.status) === 'READY';

  const grouped = useMemo(() => {
      const base = { RAISED: [], PREPARING: [], READY: [], SERVED: [] };
    rows.forEach((r) => {
      const key = normalizeStatus(r.status);
      if (base[key]) base[key].push(r);
    });
    return base;
  }, [rows]);

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px', fontSize: '0.9rem' };
  const btnStyle = { padding: '0.5rem 0.8rem', border: 'none', borderRadius: '6px', background: 'var(--theme-primary-dark)', color: '#fff', cursor: 'pointer', fontSize: '0.85rem' };

  const columnColors = {
    RAISED: { bg: '#fff5f5', border: '#e74c3c', headerBg: '#e74c3c', icon: '🔴' },
    PREPARING: { bg: '#fff9f0', border: '#f39c12', headerBg: '#f39c12', icon: '⚙️' },
    READY: { bg: '#f0fff4', border: '#27ae60', headerBg: '#27ae60', icon: '✅' },
    SERVED: { bg: '#f7fafc', border: '#95a5a6', headerBg: '#95a5a6', icon: '📦' }
  };

  return (
    <div style={{ padding: isMobile ? '0.75rem' : '1.5rem', background: '#ecf0f1', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: '1.2rem', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '0.5rem' : 0 }}>
        <h2 style={{ margin: 0, color: '#2c3e50', fontSize: isMobile ? '1.2rem' : '1.8rem', fontWeight: 700 }}>Bar Station Display</h2>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <div style={{ background: '#fff', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.9rem', color: '#7f8c8d' }}>
            Active Orders: <strong style={{ color: '#2c3e50' }}>{rows.length}</strong>
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div style={{ background: '#fff', padding: '1rem', borderRadius: '10px', marginBottom: '1.2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.7rem', marginBottom: '0.7rem' }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={inputStyle}>
            <option value="">📊 All Status</option>
            <option value="RAISED">🔴 New Orders</option>
            <option value="PREPARING">⚙️ Preparing</option>
            <option value="READY">✅ Ready</option>
            <option value="SERVED">📦 Served</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.55rem' }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            <span style={{ fontSize: '0.9rem' }}>🔄 Auto Refresh (5s)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.55rem' }}>
            <input type="checkbox" checked={audioEnabled} onChange={async (e) => {
              if (e.target.checked) { await unlockAudio(); playChime(1); }
              setAudioEnabled(e.target.checked);
            }} />
            <span style={{ fontSize: '0.9rem' }}>🔊 Sound Alerts</span>
            <span title={wsConnected ? 'Live — instant new-order alerts' : 'Reconnecting… using 5s refresh'}
              style={{ width: 9, height: 9, borderRadius: '50%', background: wsConnected ? '#27ae60' : '#cbd5e1', display: 'inline-block' }} />
          </label>
        </div>
        <button type="button" onClick={loadBarOrders} style={{ ...btnStyle, background: '#34495e' }}>🔄 Refresh Now</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: isMobile ? '0.6rem' : '1rem' }}>
        {['RAISED', 'PREPARING', 'READY', 'SERVED'].map((col) => (
          <div key={col} style={{ background: columnColors[col].bg, borderRadius: '10px', border: `2px solid ${columnColors[col].border}`, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ background: columnColors[col].headerBg, color: '#fff', padding: '0.8rem', fontWeight: 700, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{columnColors[col].icon} {col}</span>
              <span style={{ background: 'rgba(255,255,255,0.3)', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.85rem' }}>{grouped[col].length}</span>
            </div>
            <div style={{ padding: isMobile ? '0.5rem' : '0.8rem', display: 'grid', gap: isMobile ? '0.5rem' : '0.8rem', maxHeight: isMobile ? 'none' : 'calc(100vh - 320px)', overflowY: 'auto' }}>
              {grouped[col].length === 0 ? (
                <div style={{ color: '#95a5a6', textAlign: 'center', padding: '2rem 0', fontSize: '0.9rem' }}>No orders</div>
              ) : grouped[col].map((k) => {
                const isNew = newOrderIds.has(k.id);
                return (
                <div key={k.id} style={{ background: isNew ? '#fffbeb' : '#fff', borderRadius: '8px', padding: '0.8rem', boxShadow: isNew ? '0 0 0 3px #fde68a' : '0 1px 3px rgba(0,0,0,0.1)', border: `1px solid ${isNew ? '#f59e0b' : '#e8e8e8'}`, transition: 'box-shadow 0.3s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2c3e50' }}>
                      Order #{k.kot_no}
                      {isNew && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: '#f59e0b', color: '#fff', fontSize: '0.62rem', fontWeight: 800, verticalAlign: 'middle' }}>NEW</span>}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: getAgeColor(k.raised_at), fontWeight: 600 }}>
                      🕐 {formatAge(k.raised_at)}
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginBottom: '0.5rem', display: 'grid', gap: '0.2rem' }}>
                    {k.table_no && <div><strong>Table:</strong> {k.table_no}</div>}
                    {k.check_display && <div><strong>Check:</strong> {k.check_display}</div>}
                    {k.order_type && <div><strong>Type:</strong> {k.order_type.replace(/_/g, ' ')}</div>}
                    {k.guest_name && <div><strong>Guest:</strong> {k.guest_name}</div>}
                    {k.room_number && <div><strong>Room:</strong> {k.room_number}</div>}
                  </div>

                  {Array.isArray(k.items) && k.items.length > 0 && (
                    <div style={{ background: '#f8f9fa', borderRadius: '6px', padding: '0.6rem', marginBottom: '0.6rem', marginTop: '0.6rem' }}>
                      <div style={{ fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.85rem', color: '#34495e' }}>Items:</div>
                      {k.items.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.3rem', paddingBottom: '0.3rem', borderBottom: idx < k.items.length - 1 ? '1px solid #e8e8e8' : 'none' }}>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: 600 }}>◾ {item.item_name || 'Item'}</span>
                            {item.special_instructions && <div style={{ fontSize: '0.75rem', color: '#e74c3c', marginTop: '0.1rem' }}>Note: {item.special_instructions}</div>}
                          </div>
                          <div style={{ fontWeight: 600, color: '#2c3e50', marginLeft: '0.5rem' }}>x{item.qty || 1}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {canStart(k) && (
                      <button onClick={() => performAction(k.id, 'start')} style={{ ...btnStyle, background: '#3498db', flex: 1, fontSize: '0.8rem' }}>▶ Start</button>
                    )}
                    {canReady(k) && (
                      <button onClick={() => performAction(k.id, 'ready')} style={{ ...btnStyle, background: '#f39c12', flex: 1, fontSize: '0.8rem' }}>✓ Ready</button>
                    )}
                    {canServe(k) && (
                      <button onClick={() => performAction(k.id, 'served')} style={{ ...btnStyle, background: '#27ae60', flex: 1, fontSize: '0.8rem' }}>🎉 Served</button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default BarStationDisplay;
